/**
 * RelationshipPicker — the wizard's "how did you work together?" option pills (design-spec/05 Screen
 * 02 relationship step). Selected = coral. Exposed as a real radiogroup (roving `aria-checked`) so
 * it is keyboard-operable and announces its selection, not just a row of buttons.
 *
 * The chosen value feeds the backend `relationshipContext`, which shapes the AI prompt + the letter's
 * framing (ux-flows §1.D.2), so it is a real field, not decoration.
 */
import { cn } from '@/lib/utils';

/** The canonical relationships (ux-flows §1.D.2 / design-spec Screen 02). */
export const RELATIONSHIPS = ['Manager', 'Colleague', 'Client', 'Mentor', 'Direct report', 'Other'] as const;
export type Relationship = (typeof RELATIONSHIPS)[number];

interface RelationshipPickerProps {
  value: string | null;
  onChange: (value: Relationship) => void;
}

export function RelationshipPicker({ value, onChange }: RelationshipPickerProps) {
  return (
    <div role="radiogroup" aria-label="How did you work together?" className="mt-2 flex flex-wrap gap-2">
      {RELATIONSHIPS.map((option) => {
        const selected = value === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option)}
            className={cn(
              'rounded-pill border px-4 py-2 font-display text-[13.5px] font-medium transition-colors duration-ui ease-thread',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
              selected
                ? 'border-coral bg-coral-tint text-coral-text'
                : 'border-line-2 bg-surface text-ink hover:bg-paper-2',
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}
